import { useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import ProfesionalModal from '../../../RGRL/components/ProfesionalModal';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContratistaModal from './ContratistaModal';
import { useGetTiposResolucionQuery } from '../../../../services/avisoObraApi';

interface StepThreeProps {
    t: any;
    formData: any;
    handleChange: (event: { target: { name: string; value: any } }) => void;
    isView?: boolean;
}

const StepThree = ({ t, formData, handleChange, isView }: StepThreeProps) => {
    const [openModal, setOpenModal] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [errorCargo, setErrorCargo] = useState<string | null>(null);
    const [openContratista, setOpenContratista] = useState(false);
    const contratistas = formData.contratistas || [];

    const responsables = formData.responsables || [];
    const [editContratista, setEditContratista] = useState<any>(null);
    const [editContratistaIndex, setEditContratistaIndex] = useState<number | null>(null);
    const { data: resoluciones = [] } = useGetTiposResolucionQuery();
    const hasProfesionalHigiene = responsables.some((r: any) => r.idCargo === 2);
    const hasResponsableFormulario = responsables.some((r: any) => r.idCargo === 1);
    const resolucionSeleccionada = resoluciones.find(
        (r: any) => r.id === formData.idTipoResolucion
    );

    const handleOpenAdd = () => {
        setEditData(null);
        setEditIndex(null);
        setOpenModal(true);
        setErrorCargo(null);
    };

    const handleOpenEdit = (index: number) => {
        setEditIndex(index);
        setEditData(responsables[index]);
        setOpenModal(true);
        setErrorCargo(null);
    };

    const handleDelete = (index: number) => {
        const updated = [...responsables];
        updated.splice(index, 1);
        handleChange({ target: { name: 'responsables', value: updated } });
    };

    const handleSaveProfesional = (data: any) => {
        const isResponsable = data?.idCargo === 1;
        const responsableYaExiste = responsables.some(
            (p: any, i: any) => p.idCargo === 1 && i !== editIndex
        );

        if (isResponsable && responsableYaExiste) {
            setErrorCargo(t('step3.errors.duplicateResponsable'));
            return;
        }

        const updated = [...responsables];

        if (editIndex !== null) {
            updated[editIndex] = data;
        } else {
            updated.push(data);
        }

        handleChange({ target: { name: 'responsables', value: updated } });
        setOpenModal(false);
    };

    const handleOpenEditContratista = (index: number) => {
        const current = contratistas[index];
        if (!current) return;

        const [razonBase, tipoTexto] = current.razonSocial.split(' - ');

        setEditContratista({
            ...current,
            razonSocial: razonBase,
            tipoTexto: tipoTexto
        });
        setEditContratistaIndex(index);
        setOpenContratista(true);
    };

    const handleSaveContratista = (data: any) => {
        const updated = [...contratistas];
        if (editContratistaIndex !== null) {
            updated[editContratistaIndex] = data;
        } else {
            updated.push(data);
        }
        handleChange({ target: { name: 'contratistas', value: updated } });
        setOpenContratista(false);
        setEditContratista(null);
        setEditContratistaIndex(null);
    };

    const handleDeleteContratista = (index: number) => {
        const updated = [...contratistas];
        updated.splice(index, 1);
        handleChange({ target: { name: 'contratistas', value: updated } });
    };

    return (
        <>
            <Box
                sx={{
                    bgcolor: '#fff',
                    mb: 2
                }}
            >
                <Box
                    sx={{
                        bgcolor: '#3F5E6D',
                        px: 2,
                        py: 1
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 400, color: '#fff !important' }}
                    >
                        Responsable de la Obra - {resolucionSeleccionada?.name || ''}
                    </Typography>
                </Box>

                <Grid container spacing={2} sx={{ p: 2 }}>
                    <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {!isView && contratistas.length === 0 && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{ flex: 1, color: 'text.secondary', pr: 2 }}
                                >
                                    {t(
                                        'step3.contractorDescription',
                                        'Completar los datos del contratista'
                                    )}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setOpenContratista(true)}
                                    startIcon={<AddIcon />}
                                >
                                    Agregar
                                </Button>
                            </>
                        )}
                    </Grid>

                    {contratistas.length > 0 && (
                        <Grid item xs={12}>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#384A52' }}>
                                    <TableRow>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            CUIT
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            Razón Social
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            Acciones
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {contratistas.map((c: any, index: number) => (
                                        <TableRow key={index}>
                                            <TableCell>{c.cuitCuilCuip || c.cuit}</TableCell>
                                            <TableCell>{c.razonSocial}</TableCell>
                                            <TableCell>
                                                {!isView && (
                                                    <>
                                                        <IconButton
                                                            onClick={() =>
                                                                handleOpenEditContratista(index)
                                                            }
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() =>
                                                                handleDeleteContratista(index)
                                                            }
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box
                sx={{
                    bgcolor: '#fff',
                    mt: 2
                }}
            >
                <Box
                    sx={{
                        bgcolor: '#3F5E6D',
                        px: 2,
                        py: 1
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#fff' }}>
                        {t('step3.title')}
                    </Typography>
                </Box>

                <Grid container spacing={2} sx={{ p: 2 }}>
                    <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        {!isView && (
                            <>
                                <Typography
                                    variant="body2"
                                    sx={{ flex: 1, color: 'text.secondary', pr: 2 }}
                                >
                                    {t('step3.description')}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleOpenAdd}
                                    startIcon={<AddIcon />}
                                >
                                    {t('step3.actions.add')}
                                </Button>
                            </>
                        )}
                    </Grid>

                    {responsables.length > 0 && (
                        <Grid item xs={12}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#384A52' }}>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.nombre')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.cargo')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.cuit')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.representacion')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.tipoContratacion')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.titulo')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.matricula')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.entidad')}
                                        </TableCell>
                                        <TableCell
                                            sx={{ color: '#fff', backgroundColor: '#384A52' }}
                                        >
                                            {t('step3.labels.acciones')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {responsables.map((prof: any, index: number) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                '&:hover': { backgroundColor: '#f5f5f5' }
                                            }}
                                        >
                                            <TableCell>
                                                {prof.nombre} {prof.apellido}
                                            </TableCell>
                                            <TableCell>{prof.cargoNombre}</TableCell>
                                            <TableCell>{prof.cuit}</TableCell>
                                            <TableCell>
                                                {prof.representacionNombre || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {prof.tipoContratacionNombre || '-'}
                                            </TableCell>
                                            <TableCell>{prof.titulo || '-'}</TableCell>
                                            <TableCell>{prof.matricula || '-'}</TableCell>
                                            <TableCell>{prof.entidadOtorgante || '-'}</TableCell>
                                            <TableCell>
                                                {!isView && (
                                                    <>
                                                        <IconButton
                                                            onClick={() => handleOpenEdit(index)}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handleDelete(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {!isView && (!hasProfesionalHigiene || !hasResponsableFormulario) && (
                                <Box sx={{ mt: 2 }}>
                                    {!hasProfesionalHigiene && (
                                        <Alert severity="warning" sx={{ mb: 1 }}>
                                            Necesita cargar un{' '}
                                            <strong>Profesional de Higiene y Seguridad</strong> para
                                            poder presentar el aviso de obra.
                                        </Alert>
                                    )}
                                    {!hasResponsableFormulario && (
                                        <Alert severity="warning">
                                            Necesita cargar un{' '}
                                            <strong>Responsable de Datos del Formulario</strong>{' '}
                                            para poder presentar el aviso de obra.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Box>

            {!isView && (
                <>
                    <ProfesionalModal
                        open={openModal}
                        onClose={() => setOpenModal(false)}
                        onSubmit={handleSaveProfesional}
                        editData={editData}
                        errorCargo={errorCargo}
                        idAvisoObra={formData.idAvisoObra}
                    />
                    <ContratistaModal
                        open={openContratista}
                        onClose={() => setOpenContratista(false)}
                        onSubmit={handleSaveContratista}
                        editData={editContratista}
                        idTipoResolucion={formData.idTipoResolucion}
                    />
                </>
            )}
        </>
    );
};

export default StepThree;
